import axiosClient from '@/lib/axios';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

export const ATTENDANCE_REPORT_POLL_INTERVAL = 2000;
export const ATTENDANCE_REPORT_TIMEOUT = 5 * 60 * 1000;

export type AttendanceReportPendingStatus = 'queued' | 'waiting' | 'active';

type StartAttendanceReportResponse = {
  job_id: string;
  status: 'queued';
};

type AttendanceReportPendingResponse = {
  status: AttendanceReportPendingStatus;
};

export type AttendanceReportCompletedResponse = {
  status: 'completed';
  download_url: string;
  file_name: string;
};

type AttendanceReportFailedResponse = {
  status: 'failed';
  error: string;
};

export type AttendanceReportJobResponse =
  | AttendanceReportPendingResponse
  | AttendanceReportCompletedResponse
  | AttendanceReportFailedResponse;

type UseMeetingAttendanceReportResult = {
  data?: AttendanceReportCompletedResponse;
  dataUpdatedAt: number;
  error?: string;
  generate: () => void;
  isFailed: boolean;
  isGenerating: boolean;
  isLoading: boolean;
  retry: () => void;
  status?: AttendanceReportJobResponse['status'];
};

export const shouldPollAttendanceReport = (status?: AttendanceReportJobResponse['status']) =>
  status === 'queued' || status === 'waiting' || status === 'active';

export const startMeetingAttendanceReport = async ({
  companyID,
  meetingID,
  signal,
}: {
  companyID: number;
  meetingID: number;
  signal?: AbortSignal;
}): Promise<StartAttendanceReportResponse> => {
  const { data } = await axiosClient.get<StartAttendanceReportResponse>(
    `/companies/${companyID}/meetings/${meetingID}/meeting-attendance/attendance-report`,
    { signal },
  );
  return data;
};

export const getMeetingAttendanceReportJob = async ({
  companyID,
  meetingID,
  jobID,
  signal,
}: {
  companyID: number;
  meetingID: number;
  jobID: string;
  signal?: AbortSignal;
}): Promise<AttendanceReportJobResponse> => {
  const { data } = await axiosClient.get<AttendanceReportJobResponse>(
    `/companies/${companyID}/meetings/${meetingID}/meeting-attendance/attendance-report/${jobID}`,
    { signal },
  );
  return data;
};

export const useGetMeetingAttendeesWithShares = ({
  companyID,
  meetingID,
}: {
  companyID: number;
  meetingID?: number;
}): UseMeetingAttendanceReportResult => {
  const [generationRequest, setGenerationRequest] = useState<{
    companyID: number;
    meetingID: number;
    attempt: number;
    startedAt: number;
  }>();
  const canGenerate = Boolean(companyID && meetingID);
  const isCurrentRequest = generationRequest?.companyID === companyID && generationRequest.meetingID === meetingID;

  const generationQuery = useQuery({
    queryKey: ['vote', 'meetingAttendanceReport', 'generation', generationRequest],
    queryFn: ({ signal }) =>
      startMeetingAttendanceReport({
        companyID: generationRequest?.companyID as number,
        meetingID: generationRequest?.meetingID as number,
        signal,
      }),
    enabled: canGenerate && isCurrentRequest,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });

  const jobID = generationQuery.data?.job_id;
  const jobQuery = useQuery({
    queryKey: ['vote', 'meetingAttendanceReport', 'job', generationRequest, jobID],
    queryFn: ({ signal }) => {
      if (Date.now() - (generationRequest?.startedAt ?? 0) >= ATTENDANCE_REPORT_TIMEOUT) {
        throw new Error('Attendance report generation timed out');
      }

      return getMeetingAttendanceReportJob({
        companyID: generationRequest?.companyID as number,
        meetingID: generationRequest?.meetingID as number,
        jobID: jobID as string,
        signal,
      });
    },
    enabled: canGenerate && isCurrentRequest && Boolean(jobID),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: (query) =>
      !query.state.error && shouldPollAttendanceReport(query.state.data?.status)
        ? ATTENDANCE_REPORT_POLL_INTERVAL
        : false,
  });

  const completedReport = isCurrentRequest && jobQuery.data?.status === 'completed' ? jobQuery.data : undefined;
  const failedJob = isCurrentRequest && jobQuery.data?.status === 'failed' ? jobQuery.data : undefined;
  const requestError = isCurrentRequest ? generationQuery.error ?? jobQuery.error : undefined;
  const isGenerating =
    isCurrentRequest &&
    (generationQuery.isPending ||
      (Boolean(jobID) && !jobQuery.error && (jobQuery.isPending || shouldPollAttendanceReport(jobQuery.data?.status))));
  const isFailed = Boolean(failedJob || requestError);

  const generate = () => {
    if (!isGenerating) {
      setGenerationRequest((request) => ({
        companyID,
        meetingID: meetingID as number,
        attempt: isCurrentRequest ? (request?.attempt ?? 0) + 1 : 0,
        startedAt: Date.now(),
      }));
    }
  };

  return {
    data: completedReport,
    dataUpdatedAt: jobQuery.dataUpdatedAt,
    error: failedJob?.error ?? requestError?.message,
    isFailed,
    isGenerating,
    isLoading: isGenerating,
    generate,
    retry: generate,
    status: isCurrentRequest ? jobQuery.data?.status ?? generationQuery.data?.status : undefined,
  };
};
